/**
 * useLlmNarrator — React hook for the Coach LLM Narrator.
 *
 * Controlled hook (settings come from props; persistence lives at the
 * TradingJournal level alongside every other persisted setting) that:
 *
 *   1. Exposes a single `narrate({observations, scope, scopeId, tradeCount})`
 *      function returning the narrative text + status.
 *   2. Tracks per-scope inflight requests so components can show loading.
 *   3. Accumulates token usage + cost back into `settings` via `onChange`
 *      so the Settings UI sees current spend.
 *
 * Why controlled (not self-managing):
 *   TradingJournal owns the canonical settings object and saves it through
 *   a single debounced write to settings.json. A self-managing hook here
 *   would race that write and risk overwriting other settings sections.
 *   Pulling the state up keeps the persistence path single-source-of-truth.
 *
 * Settings shape (lives at settings.coachLLM):
 *   {
 *     enabled: boolean,
 *     apiKey: string,
 *     model: string,
 *     totalInputTokens: number,
 *     totalOutputTokens: number,
 *     totalSpendUSD: number,
 *     callCount: number,
 *     lastCalledAt: string|null,
 *   }
 */

import { useCallback, useRef, useState } from 'react';
import { narrate as narrateService, clearNarrationCache } from '../services/coach/llm/llmNarrator';
import { DEFAULT_MODEL } from '../services/coach/llm/anthropicClient';
import { DEFAULT_VOICE } from '../services/coach/llm/voicePersonalities';

export const DEFAULT_LLM_SETTINGS = Object.freeze({
  enabled: false,
  apiKey: '',
  model: DEFAULT_MODEL,
  voice: DEFAULT_VOICE,         // 'mentor' | 'analyst' | 'drill' | 'friend'
  ttsEnabled: false,            // Phase 15 — master toggle for speaking aloud
  // Phase 16 — TTS provider configuration. Each provider has its own
  // settings; the dispatcher in coachTTS.js routes by `provider`.
  tts: {
    provider: 'browser',                  // 'browser' | 'openai' | 'elevenlabs'
    // Browser provider (free, varies by host quality)
    browserVoiceURI: '',                  // empty → auto-pick best available
    // OpenAI provider (~$15/1M chars, very natural)
    openaiApiKey: '',
    openaiModel: 'tts-1',                 // or 'tts-1-hd' for higher quality
    openaiVoiceMap: {
      mentor: 'nova', analyst: 'alloy', drill: 'onyx', friend: 'shimmer',
    },
    // ElevenLabs provider (best quality, ~$0.02-0.05/paragraph)
    elevenlabsApiKey: '',
    elevenlabsModel: 'eleven_turbo_v2_5', // fast + cheap default
    elevenlabsVoiceMap: {
      mentor: '21m00Tcm4TlvDq8ikWAM',     // Rachel
      analyst: 'pNInz6obpgDQGcFmaJgB',    // Adam
      drill: 'VR6AewLTigWG4xSOukaG',      // Arnold
      friend: 'EXAVITQu4vr4xnSDxMaL',     // Bella
    },
  },
  totalInputTokens: 0,
  totalOutputTokens: 0,
  totalCacheCreationTokens: 0,  // tokens written to prompt cache
  totalCacheReadTokens: 0,      // tokens served from prompt cache
  totalSpendUSD: 0,
  callCount: 0,
  lastCalledAt: null,
});

const useLlmNarrator = ({ settings = DEFAULT_LLM_SETTINGS, onChange } = {}) => {
  // Per-scope inflight requests so multiple cards can each have their own
  // loading state. Key: `${scope}|${scopeId}`. Value: AbortController.
  const inflightRef = useRef(new Map());
  const [inflightTick, setInflightTick] = useState(0); // re-render trigger

  const isReady = !!(settings?.enabled && settings?.apiKey);

  const narrate = useCallback(async ({
    observations,
    scope,
    scopeId,
    tradeCount,
    skipCache,
  }) => {
    if (!settings?.enabled) return { ok: false, error: 'LLM narrator disabled in Settings' };
    if (!settings?.apiKey) return { ok: false, error: 'No API key configured' };
    if (!Array.isArray(observations) || observations.length === 0) {
      return { ok: false, error: 'No observations to narrate' };
    }

    // Cancel any inflight request for the same scope so rapid Refresh
    // clicks don't stack tokens.
    const inflightKey = `${scope}|${scopeId || ''}`;
    const prior = inflightRef.current.get(inflightKey);
    if (prior) {
      try { prior.abort(); } catch { /* */ }
    }
    const controller = new AbortController();
    inflightRef.current.set(inflightKey, controller);
    setInflightTick(t => t + 1);

    let result;
    try {
      result = await narrateService({
        observations, scope, scopeId, tradeCount,
        apiKey: settings.apiKey,
        model: settings.model,
        voice: settings.voice || DEFAULT_VOICE,
        signal: controller.signal,
        skipCache,
      });
    } finally {
      if (inflightRef.current.get(inflightKey) === controller) {
        inflightRef.current.delete(inflightKey);
        setInflightTick(t => t + 1);
      }
    }

    // Accumulate spend telemetry on a successful, non-cached API call.
    // Cache tokens are tracked separately so the Settings UI can show
    // cache hit ratio (a useful "is the prompt cache working?" check).
    if (result.ok && !result.fromCache && typeof onChange === 'function') {
      onChange({
        ...settings,
        totalInputTokens: (settings.totalInputTokens || 0) + (result.inputTokens || 0),
        totalOutputTokens: (settings.totalOutputTokens || 0) + (result.outputTokens || 0),
        totalCacheCreationTokens: (settings.totalCacheCreationTokens || 0) + (result.cacheCreationTokens || 0),
        totalCacheReadTokens: (settings.totalCacheReadTokens || 0) + (result.cacheReadTokens || 0),
        totalSpendUSD: (settings.totalSpendUSD || 0) + (result.costUSD || 0),
        callCount: (settings.callCount || 0) + 1,
        lastCalledAt: result.generatedAt || new Date().toISOString(),
      });
    }
    return result;
  }, [settings, onChange]);

  const isLoading = useCallback((scope, scopeId) => {
    void inflightTick;  // referenced so React knows the closure depends on it
    return inflightRef.current.has(`${scope}|${scopeId || ''}`);
  }, [inflightTick]);

  const clearCache = useCallback(() => clearNarrationCache(), []);

  const resetSpend = useCallback(() => {
    if (typeof onChange !== 'function') return;
    onChange({
      ...settings,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalCacheCreationTokens: 0,
      totalCacheReadTokens: 0,
      totalSpendUSD: 0,
      callCount: 0,
      lastCalledAt: null,
    });
  }, [settings, onChange]);

  return {
    settings,
    isReady,
    narrate,
    isLoading,
    clearCache,
    resetSpend,
  };
};

export default useLlmNarrator;
